import React, { useEffect, useState } from "react";
import Button from "./Button";
import {API_BASE} from "../../config";
import { useNavigate } from "react-router-dom";

// Helper: Extracts plain text from a JSX element
const getOfferText = (offer) => {
  if (typeof offer === "string") return offer;
  if (React.isValidElement(offer)) {
    let text = "";
    React.Children.forEach(offer.props.children, (child) => {
      if (typeof child === "string") {
        text += child;
      } else if (React.isValidElement(child) && child.props.children) {
        text += child.props.children;
      }
    });
    return text.trim();
  }
  return "";
};

const fetchPincodeDetails = async (pincode) => {
  const res = await fetch(`https://api.postalpincode.in/pincode/${pincode}`);
  const data = await res.json();
  if (data[0].Status === "Success") {
    const postOffice = data[0].PostOffice[0];
    return { city: postOffice.District, state: postOffice.State };
  } else {
    throw new Error("Invalid Pincode");
  }
};

const DetailsModal = ({ isOpen, onClose, onSubmit }) => {
  const initialFormData = {
    name: "",
    mobile: "",
    email: "",
    pincode: "",
    city: "",
    state: "",
    address: ""
  };
  const [formData, setFormData] = useState(initialFormData);

  useEffect(() => {
    if (!isOpen) setFormData(initialFormData);
  }, [isOpen]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  useEffect(() => {
    const pincode = formData.pincode.trim();
    if (pincode.length === 6) {
      fetchPincodeDetails(pincode)
        .then((result) => {
          setFormData((prev) => ({
            ...prev,
            city: result.city,
            state: result.state
          }));
        })
        .catch(() => {
          setFormData((prev) => ({ ...prev, city: "", state: "" }));
          alert("Invalid Pincode. Please check and try again.");
        });
    }
  }, [formData.pincode]);

  const handleSubmit = (e) => {
    e.preventDefault();
    onSubmit(formData);
    onClose();
  };

  if (!isOpen) return null;
  return (
<div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-md px-4">
  <div
    className="
      relative
      w-full
      max-w-md
      max-h-[90vh]
      overflow-y-auto
      rounded-[28px]
      bg-white/90
      backdrop-blur-2xl
      border
      border-white/50
      shadow-[0_30px_80px_rgba(20,10,0,.18)]
    "
  >
    {/* HEADER */}
    <div className="bg-gradient-to-r from-[#5D7A4D] to-[#48603C] px-6 py-5">
      <button
        onClick={onClose}
        className="
          absolute
          top-4
          right-4
          w-8
          h-8
          rounded-full
          bg-white/10
          border
          border-white/10
          text-white
          text-lg
          flex
          items-center
          justify-center
          hover:bg-white/20
          transition-all
        "
      >
        ×
      </button>

      <div
        className="
          inline-flex
          px-3
          py-1.5
          rounded-full
          bg-white/10
          border
          border-white/10
          text-white
          text-[10px]
          tracking-[.08em]
          mb-3
        "
      >
        REGISTRATION DETAILS
      </div>

      <h2 className="text-2xl text-white font-semibold leading-none">
        Complete Your Booking
      </h2>
    </div>

    {/* FORM */}
    <div className="p-5">
      <form
        onSubmit={handleSubmit}
        className="space-y-3"
      >
        {/* NAME */}
        <div>
          <label className="block text-[#6B574A] text-xs mb-1">
            Full Name
          </label>

          <input
            type="text"
            name="name"
            value={formData.name}
            onChange={handleChange}
            required
            className="
              w-full
              h-11
              px-4
              rounded-xl
              bg-[#FAF8F5]
              border
              border-[#ECE3D9]
              focus:outline-none
              focus:border-primary
            "
          />
        </div>

        {/* MOBILE */}
        <div>
          <label className="block text-[#6B574A] text-xs mb-1">
            Mobile Number
          </label>

          <input
            type="tel"
            name="mobile"
            value={formData.mobile}
            onChange={handleChange}
            required
            className="
              w-full
              h-11
              px-4
              rounded-xl
              bg-[#FAF8F5]
              border
              border-[#ECE3D9]
              focus:outline-none
              focus:border-primary
            "
          />
        </div>

        {/* EMAIL */}
        <div>
          <label className="block text-[#6B574A] text-xs mb-1">
            Email Address
          </label>

          <input
            type="email"
            name="email"
            value={formData.email}
            onChange={handleChange}
            required
            className="
              w-full
              h-11
              px-4
              rounded-xl
              bg-[#FAF8F5]
              border
              border-[#ECE3D9]
              focus:outline-none
              focus:border-primary
            "
          />
        </div>

        {/* PINCODE */}
        <div>
          <label className="block text-[#6B574A] text-xs mb-1">
            Pincode
          </label>

          <input
            type="text"
            name="pincode"
            value={formData.pincode}
            onChange={handleChange}
            required
            className="
              w-full
              h-11
              px-4
              rounded-xl
              bg-[#FAF8F5]
              border
              border-[#ECE3D9]
              focus:outline-none
              focus:border-primary
            "
          />
        </div>

        {/* CITY + STATE */}
        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className="block text-[#6B574A] text-xs mb-1">
              City
            </label>

            <input
              type="text"
              name="city"
              value={formData.city}
              onChange={handleChange}
              required
              className="
                w-full
                h-11
                px-4
                rounded-xl
                bg-[#FAF8F5]
                border
                border-[#ECE3D9]
                focus:outline-none
                focus:border-primary
              "
            />
          </div>

          <div>
            <label className="block text-[#6B574A] text-xs mb-1">
              State
            </label>

            <input
              type="text"
              name="state"
              value={formData.state}
              onChange={handleChange}
              required
              className="
                w-full
                h-11
                px-4
                rounded-xl
                bg-[#FAF8F5]
                border
                border-[#ECE3D9]
                focus:outline-none
                focus:border-primary
              "
            />
          </div>
        </div>

        {/* ADDRESS */}
        <div>
          <label className="block text-[#6B574A] text-xs mb-1">
            Address (Optional)
          </label>

          <textarea
            rows="2"
            name="address"
            value={formData.address}
            onChange={handleChange}
            className="
              w-full
              px-4
              py-3
              rounded-xl
              bg-[#FAF8F5]
              border
              border-[#ECE3D9]
              resize-none
              focus:outline-none
              focus:border-primary
            "
          />
        </div>

        {/* BUTTON */}
        <div className="pt-1">
          <Button
            type="submit"
            text="Proceed To Payment"
            variant="primary"
            size="md"
          />
        </div>
      </form>
    </div>
  </div>
</div>
  );
};

const PaymentSummaryModal = ({
  paymentOption,
  isOpen,
  onBack,
  onPay,
  baseTotal,
  discountedAmount ,
  halfpay,total, // For full: fullDiscountedAmount; for partial: partialDiscountedAmount
}) => {
  if (!isOpen) return null;

  if (paymentOption === "partial") {
    // For partial payment, no extra 10% discount is applied.
    const discountApplied = total-baseTotal
    
    ;
    const halfAmount = halfpay ;
    const partialGST = discountedAmount * 0.18;
    const partialFinalPrice = discountedAmount+partialGST;

    return (
  <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-md px-4">
    <div
      className="
        w-full
        max-w-2xl
        rounded-[34px]
        bg-white/90
        backdrop-blur-2xl
        border
        border-white/50
        shadow-[0_30px_80px_rgba(20,10,0,.18)]
        overflow-hidden
      "
    >
      <div className="bg-gradient-to-r from-[#5D7A4D] to-[#48603C] px-8 py-7">
        <div className="inline-flex px-4 py-2 rounded-full bg-white/10 border border-white/10 text-xs tracking-[.08em] text-white mb-4">
          PAYMENT SUMMARY
        </div>

        <h2 className="text-4xl text-white leading-none">
          Partial Payment
        </h2>
      </div>

      <div className="p-8">
        <div className="space-y-4">
          {[
            ["Original Price", `₹${total.toLocaleString("en-IN")}`],
            ["Discount Applied", `₹${discountApplied.toLocaleString("en-IN")}`],
            ["Discounted Price", `₹${discountedAmount.toLocaleString("en-IN")}`],
            ["Prebook Payment", `₹${halfAmount.toLocaleString("en-IN")}`],
            ["GST (18%)", `₹${partialGST.toLocaleString("en-IN")}`],
            ["Total Amount", `₹${partialFinalPrice.toLocaleString("en-IN")}`],
          ].map(([label, value], i) => (
            <div
              key={i}
              className="
                flex
                justify-between
                items-center
                rounded-2xl
                bg-[#FAF8F5]
                p-4
              "
            >
              <span className="text-[#6B574A]">{label}</span>
              <span className="font-semibold text-secondary">
                {value}
              </span>
            </div>
          ))}
        </div>

        <div
          className="
            mt-6
            rounded-[28px]
            bg-gradient-to-r
            from-[#FFF1E4]
            to-[#FFF8F2]
            p-6
            text-center
          "
        >
          <p className="text-[#6B574A] mb-2">
            Amount Paying Now
          </p>

          <h3 className="text-4xl text-primary">
            ₹{halfAmount.toLocaleString("en-IN")}
          </h3>
        </div>

        <div className="flex gap-4 mt-8">
          <Button
            onClick={onBack}
            text="Back"
            variant="secondary"
            size="md"
          />

          <Button
            onClick={onPay}
            text="Pay Now"
            variant="primary"
            size="md"
          />
        </div>
      </div>
    </div>
  </div>
);
    
  } else {
    // For full payment, the extra 10% discount applies.
    const discountApplied =((total- baseTotal)+baseTotal*0.05).toFixed(2);
    const fullGST = (discountedAmount*0.95) * 0.18;
    const fullFinalPrice = discountedAmount*0.95 + fullGST;
return (
  <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-md px-4">
    <div
      className="
        w-full
        max-w-2xl
        rounded-[34px]
        bg-white/90
        backdrop-blur-2xl
        border
        border-white/50
        shadow-[0_30px_80px_rgba(20,10,0,.18)]
        overflow-hidden
      "
    >
      <div className="bg-gradient-to-r from-[#5D7A4D] to-[#48603C] px-8 py-7">
        <div className="inline-flex px-4 py-2 rounded-full bg-white/10 border border-white/10 text-xs tracking-[.08em] text-white mb-4">
          PAYMENT SUMMARY
        </div>

        <h2 className="text-4xl text-white leading-none">
          Full Payment
        </h2>
      </div>

      <div className="p-8">
        <div className="space-y-4">
          {[
            ["Original Price", `₹${total.toLocaleString("en-IN")}`],
            ["Discount Applied", `₹${discountApplied}`],
            ["GST (18%)", `₹${fullGST.toLocaleString("en-IN")}`],
            [
              "Discounted Price",
              `₹${(discountedAmount * 0.95).toFixed(2)}`
            ],
          ].map(([label, value], i) => (
            <div
              key={i}
              className="
                flex
                justify-between
                items-center
                rounded-2xl
                bg-[#FAF8F5]
                p-4
              "
            >
              <span className="text-[#6B574A]">{label}</span>
              <span className="font-semibold text-secondary">
                {value}
              </span>
            </div>
          ))}
        </div>

        <div
          className="
            mt-6
            rounded-[28px]
            bg-gradient-to-r
            from-[#FFF1E4]
            to-[#FFF8F2]
            p-6
            text-center
          "
        >
          <p className="text-[#6B574A] mb-2">
            Final Amount
          </p>

          <h3 className="text-4xl text-primary">
            ₹{fullFinalPrice.toFixed(2)}
          </h3>
        </div>

        <div className="flex gap-4 mt-8">
          <Button
            onClick={onBack}
            text="Back"
            variant="secondary"
            size="md"
          />

          <Button
            onClick={onPay}
            text="Pay Now"
            variant="primary"
            size="md"
          />
        </div>
      </div>
    </div>
  </div>
);
  }
};

const PaymentResult = ({ success, email, billDetails, onDownloadBill, onEmailBill, onReturnHome }) => {
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-gray-900 bg-opacity-80 px-4">
      <div className="bg-white rounded-lg shadow-lg max-w-md w-full p-6 text-center">
        {success ? (
          <>
            <h2 className="text-2xl font-bold text-green-600 mb-4">Payment Successful!</h2>
            <p className="mb-4">
              Your payment was processed successfully. A receipt can be sent to <strong>{email}</strong>.
            </p>
            <p className="mb-6">Download your bill details now</p>
            <div className="space-y-4 mb-6">
              <Button onClick={onDownloadBill} text="Download Bill" variant="secondary" className="mr-3" size="sm" />
              <Button onClick={onEmailBill} text="Email Bill" variant="secondary" size="sm" />
            </div>
          </>
        ) : (
          <>
            <h2 className="text-2xl font-bold text-red-600 mb-4">Payment Failed!</h2>
            <p className="mb-6">There was an error processing your payment. Please try again.</p>
          </>
        )}
        <a href="/" className="text-primary font-bold hover:text-secondary underline">Return to Home</a>
      </div>
    </div>
  );
};


const PricingSection = ({ data, validCoupons, dueDate,program }) => {
  const navigate = useNavigate();

  const buttonText = "Reserve Your Spot";
  const [quantities, setQuantities] = useState(data.packages.map(() => 0));
  const [paymentOption, setPaymentOption] = useState("partial");
  const [selectedOffer, setSelectedOffer] = useState("");
  const [specialOfferDiscount, setSpecialOfferDiscount] = useState(0);
  const [couponDiscount, setCouponDiscount] = useState(1);
  const [showDetailsModal, setShowDetailsModal] = useState(false);
  const [showSummaryModal, setShowSummaryModal] = useState(false);
  const [showPackageMessage, setShowPackageMessage] = useState(false);
  const [couponCode, setCouponCode] = useState("");
  const [showPaymentResult, setShowPaymentResult] = useState(false);
  const [paymentSuccess, setPaymentSuccess] = useState(false);
  const [userDetails, setUserDetails] = useState({});
  const [invoiceOrderId, setInvoiceOrderId] = useState("");

  // Prepare offers data
  const offersData = data.offers.map((offer) => ({
    label: offer.label,
    discount: offer.discount,
    validUntil: offer.validUntil,
    minQuantity: offer.minQuantity
  }));

  const handleQuantityChange = (index, delta) => {
    setQuantities((prev) => {
      const newQuantities = [...prev];
      newQuantities[index] = Math.max(0, newQuantities[index] + delta);
      return newQuantities;
    });
  };
    const Total = quantities.reduce((sum, qty, index) => {
    const price = parseFloat(String(data.packages[index].standardPrice).replace(/,/g, ""));
    return sum + qty * (isNaN(price) ? 0 : price);
  }, 0);


  const baseTotal = quantities.reduce((sum, qty, index) => {
    const price = parseFloat(String(data.packages[index].discountedPrice).replace(/,/g, ""));
    return sum + qty * (isNaN(price) ? 0 : price);
  }, 0);
const prebook=5000;
const totalQuantity = quantities.reduce((a, b) => a + b, 0);
let halfpay = prebook*totalQuantity;

  const fullDiscountedAmount = baseTotal  * (1 - specialOfferDiscount) ;
  let amountAfterDiscount, gstAmount, finalAmount;
  if (paymentOption === "full") {
    amountAfterDiscount = fullDiscountedAmount*0.95;
    gstAmount = (fullDiscountedAmount*0.95) * 0.18;
    finalAmount = fullDiscountedAmount*0.95 + gstAmount;
  } else {
    // For partial payment, show half-payment info
    amountAfterDiscount = fullDiscountedAmount; // full discounted price without extra 10%
    gstAmount = fullDiscountedAmount* 0.18;
    finalAmount = halfpay;
  }

let rem = ((fullDiscountedAmount+fullDiscountedAmount*0.18) - halfpay) ;
  const openDetailsModal = () => {
    if (baseTotal === 0) {
      setShowPackageMessage(true);
      setTimeout(() => setShowPackageMessage(false), 3000);
      return;
    }
    setShowDetailsModal(true);
  };

  const handleModalSubmit = (formData) => {
    console.log("User Details Submitted:", formData);
    setUserDetails(formData);
    setShowDetailsModal(false);
    setShowSummaryModal(true);
  };

  const handleSummaryBack = () => {
    setShowSummaryModal(false);
    setShowDetailsModal(true);
  };const handleSummaryPayNow = async () => {
    try {
      // Ensure finalAmount is computed before this function call.
      if (!finalAmount) {
        console.error("Final amount not computed.");
        return;
      }
  
      // 1. Create order from backend using finalAmount (in rupees)
      // Note: If your backend expects rupees, you must send finalAmount (not in paise).
      // Make sure you send ALL the fields your backend needs:
const orderResponse = await fetch(`${API_BASE}/order/new`, {
  method: "POST",
  headers: { "Content-Type": "application/json" },
  body: JSON.stringify({
    name:    userDetails.name,
    email:   userDetails.email,
    phone:   userDetails.mobile,
    amount:  finalAmount.toFixed(2),
  })
});

const { razorpayOrder, orderInDB } = await orderResponse.json();
// now razorpayOrder.id is your new Razorpay order id
const orderId = razorpayOrder.id;
console.log("Order Created, Order ID:", orderId);
  
      // 2. Build invoice details (using your existing logic, ensure it's defined)
      const invoiceItems = data.packages.map((pkg, index) => {
        const qty = quantities[index];
        if (qty > 0) {
          const standardPrice = parseFloat(String(pkg.standardPrice).replace(/,/g, "")) || 0;
          const discountedPrice = parseFloat(String(pkg.discountedPrice).replace(/,/g, "")) || 0;
          return {
            name: pkg.name,
            quantity: qty,
            standardPrice:( standardPrice).toFixed(2),
            discountedPrice: (discountedPrice ).toFixed(2),
          };
        }
        return null;
      }).filter(item => item);
      const overallDiscountedTotal = invoiceItems.reduce(
        (sum, item) => sum + (parseFloat(item.discountedPrice) * item.quantity || 0),
        0
      );      const total = invoiceItems.reduce(
        (sum, item) => sum + (parseFloat(item.standardPrice) * item.quantity || 0),
        0
      );
const dispercentage = (((total-baseTotal)/total)* 100).toFixed(2);
const dispercent = paymentOption === "full" 
  ? (parseFloat(dispercentage) + 5).toFixed(2)
  : dispercentage;

      const fullamount=(fullDiscountedAmount*0.95+((fullDiscountedAmount*0.95)*0.18));
      const invoiceDetails = {
        orderId,
         program:program,

        date: new Date().toLocaleDateString(),
        dueDate: dueDate || new Date(new Date().setDate(new Date().getDate() )).toLocaleDateString(),
        ...userDetails,
        items: invoiceItems,
        overallDiscountedTotal: total.toFixed(2),
        discountApplied:dispercent,
        payableAmount:
          paymentOption === "full"
            ? fullDiscountedAmount*0.95.toFixed(2)
            : (fullDiscountedAmount ).toFixed(2),
        gst:(gstAmount).toFixed(2),
totalPayable:paymentOption === "full"?fullamount.toFixed(2):(fullDiscountedAmount+fullDiscountedAmount*0.18).toFixed(2),
paidAmount: paymentOption === "partial" ? (halfpay).toFixed(2) : (fullamount).toFixed(2),
remainingAmount: paymentOption === "partial" 
  ? (rem).toFixed(2)
  : "0",

      };
  
      // 3. Set up Razorpay payment options using finalAmount (make sure amount is in paise)
      const options = {
        key: process.env.REACT_APP_RAZORPAY_KEY,
        amount: Math.round(finalAmount * 100), // Convert rupees to paise
        name: "PARAM LIFE WELLNESS LIMITED",
        description: "Payment for your order",
        order_id: orderId,
        handler: async function (response) {
          // response.razorpay_order_id, response.razorpay_payment_id, response.razorpay_signature
          await fetch(`${API_BASE}/order/verify`, {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({
              razorpay_order_id:   response.razorpay_order_id,
              razorpay_payment_id: response.razorpay_payment_id,
              razorpay_signature:  response.razorpay_signature
            })
          });
          // …
        
          console.log("Payment Verified!");
  
          // Generate the invoice PDF using the invoiceDetails object
          await fetch(`${API_BASE}/generate-invoice`, {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify(invoiceDetails)
          });
          console.log("Invoice generated");
  
          // Update UI accordingly
          setInvoiceOrderId(orderId);
          setPaymentSuccess(true);
          setShowSummaryModal(false);
          setShowPaymentResult(true);
        },
        prefill: {
          name: userDetails.name ,
          email: userDetails.email ,
          contact: userDetails.mobile
        },
        theme: { color: "#F58020" }
      };
  
      const rzp = new window.Razorpay(options);
      rzp.open();
    } catch (error) {
      console.error("Payment initiation error:", error);
      alert("Payment initiation failed. Please try again.");
      setPaymentSuccess(false);
      setShowSummaryModal(false);
      setShowPaymentResult(true);
    }
  };
  
  const handleApplyOffer = () => {
    const idx = parseInt(selectedOffer, 10);
    if (isNaN(idx) || !offersData[idx]) {
      setSpecialOfferDiscount(0);
      return;
    }
    const offer = offersData[idx];

    if (offer.validUntil && new Date() > new Date(offer.validUntil)) {
      alert("Offer expired. Please select another offer.");
      setSpecialOfferDiscount(0);
      return;
    }

    if (offer.minQuantity) {
      const totalQty = quantities.reduce((a, b) => a + b, 0);
      if (totalQty < offer.minQuantity) {
        alert(`Booking for at least ${offer.minQuantity} people is required.`);
        setSpecialOfferDiscount(0);
        return;
      }
    }

    setSpecialOfferDiscount(offer.discount);
    alert(`Offer Applied: ${(offer.discount * 100).toFixed(0)}% off`);
  };


  const handleDownloadBill = () => {
    if (!invoiceOrderId) {
      alert("Invoice not generated yet.");
      return;
    }
    const invoiceUrl = `${API_BASE}/invoices/invoice_${invoiceOrderId}.pdf`;
    window.open(invoiceUrl, "_blank");
  };

  const handleEmailBill = async () => { 
    if (!invoiceOrderId) {
      alert("Invoice not generated yet.");
      return;
    }
    try {
      const response = await fetch(`${API_BASE}/email-invoice`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          orderId: invoiceOrderId,
          email: userDetails.email,    // Customer email from DetailsModal
          name: userDetails.name,      // Customer name from DetailsModal
          programName: program         // Dynamic program name passed as a prop
        }),
      });
      const data = await response.json();
      if (response.ok) {
        alert("Invoice emailed successfully!");
      } else {
        alert("Failed to email invoice: " + data.error);
      }
    } catch (error) {
      console.error("Error emailing invoice:", error);
      alert("Error emailing invoice. Please try again.");
    }
  };
      
  const handleReturnHome = () => {
    window.location.href = "/";
  };
return (
  <section
    className="
      py-24
      px-4
      bg-[#FAF8F5]
      relative
      overflow-hidden
    "
  >
    <div className="absolute top-[-120px] left-[-120px] w-[400px] h-[400px] rounded-full bg-[#F28C28]/10 blur-[120px]" />

    <div
      className="
        max-w-4xl
        mx-auto
        relative
        z-10
      "
    >
      {/* HEADER */}

      <div className="text-center mb-10">
        <div
          className="
            inline-flex
            px-4
            py-2
            rounded-full
            bg-[#FFF1E4]
            text-[#F28C28]
            text-xs
            tracking-[.08em]
            font-semibold
            mb-5
          "
        >
          RETREAT BOOKING
        </div>

        <h2
          className="
            text-[clamp(42px,6vw,72px)]
            leading-[1]
            tracking-[-0.05em]
            text-secondary
            mb-4
          "
        >
          Choose Your
          <br />
          Experience
        </h2>

        <p
          className="
            max-w-[650px]
            mx-auto
            text-[#6B574A]
            leading-8
          "
        >
          Select your preferred accommodation and reserve your
          place for this transformational retreat experience.
        </p>
      </div>

      {/* MAIN CARD */}

      <div
        className="
          rounded-[34px]
          bg-white/70
          backdrop-blur-2xl
          border
          border-white/40
          shadow-[0_30px_80px_rgba(20,10,0,.10)]
          overflow-hidden
        "
      >
        <div
          className="
            bg-gradient-to-br
            from-[#5D7A4D]
            to-[#48603C]
            p-8
            text-white
          "
        >
          <p className="text-center text-white/80 mb-6">
            Reserve your spot today
          </p>

          <div className="space-y-4">
            {data.packages.map((pkg, index) => (
              <div
                key={index}
                className="
                  rounded-2xl
                  bg-white/10
                  backdrop-blur-xl
                  border
                  border-white/10
                  p-5
                  flex
                  justify-between
                  items-center
                "
              >
                <div>
                  <h3 className="text-lg font-semibold text-white">
                    {pkg.name}
                  </h3>

                  <div className="flex gap-3 mt-2">
                    <span className="line-through text-white/50">
                      ₹{pkg.standardPrice}
                    </span>

                    <span className="font-bold text-[#FFD98E]">
                      ₹{pkg.discountedPrice}
                    </span>
                  </div>
                </div>

                <div className="flex items-center gap-3">
                  <button
                    onClick={() =>
                      handleQuantityChange(index, -1)
                    }
                    className="
                      w-10
                      h-10
                      rounded-full
                      bg-white/15
                      border
                      border-white/15
                      text-white
                    "
                  >
                    −
                  </button>

                  <span className="font-bold text-lg">
                    {quantities[index]}
                  </span>

                  <button
                    onClick={() =>
                      handleQuantityChange(index, 1)
                    }
                    className="
                      w-10
                      h-10
                      rounded-full
                      bg-white/15
                      border
                      border-white/15
                      text-white
                    "
                  >
                    +
                  </button>
                </div>
              </div>
            ))}
          </div>

          {/* PAYMENT OPTIONS */}

          <div className="mt-4">
            {paymentOption === "full" ? (
              <>
                <div className="text-right font-bold text-white">
                  Amount after discount: ₹{fullDiscountedAmount.toLocaleString("en-IN")}
                </div>
                                <div className="text-right font-bold text-white mt-2">
                  Amount after 5% discount: ₹{fullDiscountedAmount*0.95.toLocaleString("en-IN")}
                </div>

                <div className="text-right font-bold text-white mt-2">
                  18% GST Amount: ₹{((fullDiscountedAmount*0.95) * 0.18).toLocaleString("en-IN")}
                </div>
                <div className="text-right font-bold text-white mt-2">
                  Final Payable Amount: ₹{(fullDiscountedAmount*0.95 + ((fullDiscountedAmount*0.95) * 0.18)).toFixed(2).toLocaleString("en-IN")}
                </div>
              </>
            ) : (
              <>
                <div className="text-right font-bold text-white">
                  Amount after discount: ₹{fullDiscountedAmount.toLocaleString("en-IN")}
                </div>
                <div className="text-right font-bold text-white mt-2">
                  18% GST Amount : ₹{((fullDiscountedAmount ) * 0.18).toLocaleString("en-IN")}
                </div>
                <div className="text-right font-bold text-white mt-2">
                  Current Payable Amount: ₹{(halfpay).toLocaleString("en-IN")}
                </div>
              </>
            )}
          </div>
          {/* TOTALS */}


        </div>

        <div className="p-8 text-center">
          <Button
            onClick={openDetailsModal}
            text="Reserve Your Spot"
            variant="primary"
            size="lg"
          />
        </div>
      </div>
    </div>

    <DetailsModal
      isOpen={showDetailsModal}
      onClose={() => setShowDetailsModal(false)}
      onSubmit={handleModalSubmit}
    />

    <PaymentSummaryModal
      paymentOption={paymentOption}
      isOpen={showSummaryModal}
      onBack={handleSummaryBack}
      onPay={handleSummaryPayNow}
      baseTotal={baseTotal}
      total={Total}
      discountedAmount={fullDiscountedAmount}
      halfpay={halfpay}
    />
  </section>
);
};

export default PricingSection;
